import { createFileRoute } from "@tanstack/react-router";
import {
  PromptSettingsSchema,
  ReactivationSettingsSchema,
  useFetchSettings,
} from "@/lib/api/admin.ts";
import { PromptSettings } from "@/components/settings/prompt-settings.tsx";
import { ScheduleSettings } from "@/components/settings/schedule-settings-wrapper.tsx";
import { Loader2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCurrentUser, UserRole } from "@/lib/api/user.ts";
import { useState } from "react";
import { GroupSettings } from "@/components/settings/group-settings";
import { ReactivationSettings } from "@/components/settings/reactivation-settings";
import { DevSettings } from "@/components/settings/dev-settings";
import { PostHogEvents, useTrackPage } from "@/lib/posthog-events";
import { useAbility } from "@/lib/casl/ability-context";

export const Route = createFileRoute("/_authenticated/settings")({
  component: Settings,
});

function Settings() {
  const { data: currentUser } = useCurrentUser();
  const ability = useAbility();
  const isSuperAdmin = currentUser?.role === UserRole.SUPERADMIN;
  const [selectedTab, setSelectedTab] = useState(
    isSuperAdmin ? "ai" : "schedule",
  );
  const { data: settings, isLoading, isError } = useFetchSettings(isSuperAdmin);
  useTrackPage(PostHogEvents.PAGE_SETTINGS);

  const promptSettings = settings?.find(
    (setting) => setting.name === "AI_PROMPT",
  )?.value as PromptSettingsSchema;

  const reactivationSettings = settings?.find(
    (setting) => setting.name === "REACTIVATION_PROMPT",
  )?.value as ReactivationSettingsSchema;

  const canReadGroups = ability.can("read", "groups");

  return (
    <div className="container max-w-4xl bg-white py-4">
      <h1 className="my-6 text-2xl font-bold leading-7 text-black">Settings</h1>

      <Tabs
        defaultValue={isSuperAdmin ? "ai" : "schedule"}
        value={selectedTab}
        onValueChange={setSelectedTab}
      >
        <TabsList>
          {isSuperAdmin && <TabsTrigger value="ai">AI Settings</TabsTrigger>}
          {isSuperAdmin && (
            <TabsTrigger value="reactivation">Reactivation</TabsTrigger>
          )}
          <TabsTrigger value="schedule">Schedule</TabsTrigger>
          {canReadGroups && <TabsTrigger value="groups">Groups</TabsTrigger>}
          {isSuperAdmin && <TabsTrigger value="dev">Dev</TabsTrigger>}
        </TabsList>
        {isSuperAdmin && (
          <TabsContent value="ai">
            {selectedTab === "ai" && (
              <>
                {isLoading && (
                  <div className="flex items-center justify-center p-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                )}
                {isError && (
                  <div className="rounded-md bg-red-50 p-4">
                    <p className="text-sm text-red-700">
                      Failed to load AI settings
                    </p>
                  </div>
                )}
                {!isLoading && !isError && (
                  <PromptSettings defaultValues={promptSettings} />
                )}
              </>
            )}
          </TabsContent>
        )}
        {isSuperAdmin && (
          <TabsContent value="reactivation">
            {selectedTab === "reactivation" && (
              <>
                {isLoading && (
                  <div className="flex items-center justify-center p-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                )}
                {isError && (
                  <div className="rounded-md bg-red-50 p-4">
                    <p className="text-sm text-red-700">
                      Failed to load reactivation settings
                    </p>
                  </div>
                )}
                {!isLoading && !isError && (
                  <ReactivationSettings defaultValues={reactivationSettings} />
                )}
              </>
            )}
          </TabsContent>
        )}
        <TabsContent value="schedule">
          {selectedTab === "schedule" && <ScheduleSettings />}
        </TabsContent>
        {canReadGroups && (
          <TabsContent value="groups">
            {selectedTab === "groups" && <GroupSettings />}
          </TabsContent>
        )}
        {isSuperAdmin && (
          <TabsContent value="dev">
            {selectedTab === "dev" && <DevSettings />}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
